import { Grid, Stack } from '@mui/material';
import { AvailableCriteria, BaseUser } from '@schooly/api';
import { GroupUserType } from '@schooly/api';
import { SimpleListResult } from '@schooly/api';
import { AttentionTooltipIcon, Counter } from '@schooly/style';
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import PersonCardSelectable from '../../../../components/common/PersonCard/PersonCardSelectable';
import UserCriteria from '../../../../components/common/UserCriteria';
import { ModalEmptyMainArea } from '../../../../components/uikit-components/Modal/ModalEmptyMainArea';
import RoundCard from '../../../../components/uikit-components/RoundCard';
import {
  getCriteriaArchived,
  getCriteriaArchivedTooltip,
  getCriteriaName,
} from '../../../../helpers/misc';
import { isAgeGroupMismatch, isCriteriaAgeGroupMismatch } from '../../utils';
import { getCriteriaItemKey } from '../ExternalSidebar/AvailableCriteria';

export interface UserWithCriteria extends BaseUser {
  criteria?: string[];
}

export interface GroupUsersListProps {
  groupCriteria?: AvailableCriteria[];
  groupUsers?: SimpleListResult[];
  onCriteriaClick?: (criteria: AvailableCriteria) => void;
  onUserClick?: (userId: string) => void;
  userType: GroupUserType;
  renderCustomIcon?: (userId: string) => React.ReactNode;
  emptyStub?: React.ReactNode;
  isEmpty: boolean;
  groupAgeGroupIds?: string[];
  limitedToAgeGroupIds?: string[];
}

export const GroupUsersList: FC<GroupUsersListProps> = ({
  groupCriteria,
  groupUsers,
  onCriteriaClick,
  onUserClick,
  userType,
  renderCustomIcon,
  emptyStub,
  isEmpty,
  groupAgeGroupIds = [],
  limitedToAgeGroupIds = [],
}) => {
  const { formatMessage } = useIntl();

  const groupUsersWithCriteria = useMemo(() => {
    return groupUsers?.map((user) => {
      const userCriteria = groupCriteria?.filter((c) =>
        c?.relation_ids?.includes(user.relation_id),
      );

      if (userCriteria?.length) {
        return { ...user, criteria: userCriteria.map((c) => getCriteriaName(c)) };
      }

      return user;
    });
  }, [groupUsers, groupCriteria]);

  const users: (Partial<UserWithCriteria> & SimpleListResult)[] | undefined =
    userType === 'student' ? groupUsersWithCriteria : groupUsers;

  const userListContent = useMemo(() => {
    if (emptyStub) return emptyStub;
    if (isEmpty) {
      return (
        <ModalEmptyMainArea
          label={formatMessage({
            id: userType === 'student' ? 'userType-student-plural' : 'userType-staff',
          }).toLowerCase()}
        />
      );
    }

    return (
      <Stack>
        <Grid container spacing={1}>
          {groupCriteria?.map((item) => (
            <Grid
              item
              xs={6}
              key={getCriteriaItemKey(item)}
              sx={{ '& > * > :last-child': { ml: 0 } }}
            >
              <RoundCard
                key={item.id}
                item={item}
                isSelected
                withTooltip
                onClick={onCriteriaClick}
                name={getCriteriaName(item)}
                archived={getCriteriaArchived(item)}
                archivedTooltipId={getCriteriaArchivedTooltip(item)}
              >
                <Stack direction="row" alignItems="center" gap={1} sx={{ ml: 'auto' }}>
                  <Counter disabled>{Number(item?.relation_ids?.length)}</Counter>
                  {isCriteriaAgeGroupMismatch(item, groupAgeGroupIds, limitedToAgeGroupIds) && (
                    <AttentionTooltipIcon
                      title={formatMessage({ id: 'groups-AgeGroupMismatch-Criteria' })}
                    />
                  )}
                </Stack>
              </RoundCard>
            </Grid>
          ))}
        </Grid>

        {users?.map((user) => (
          <PersonCardSelectable
            key={user.relation_id}
            user={user}
            userType={userType}
            isStaticSelected
            onClick={onUserClick}
            isListItem
            renderCustomIcon={renderCustomIcon?.(user.relation_id)}
          >
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ mr: 2.5 }}>
              {user.criteria && <UserCriteria userCriteria={user.criteria} criteriaToShow={2} />}
              {isAgeGroupMismatch(user.age_group_ids ?? [], groupAgeGroupIds) && (
                <AttentionTooltipIcon
                  title={formatMessage({ id: 'groups-AgeGroupMismatch-Student' })}
                />
              )}
            </Stack>
          </PersonCardSelectable>
        ))}
      </Stack>
    );
  }, [
    formatMessage,
    emptyStub,
    groupCriteria,
    groupAgeGroupIds,
    limitedToAgeGroupIds,
    isEmpty,
    onCriteriaClick,
    onUserClick,
    renderCustomIcon,
    userType,
    users,
  ]);

  return <Stack sx={{ mt: 2, mx: 0.25, overflow: 'scroll' }}>{userListContent}</Stack>;
};
